'use client';

import { useCallback, useEffect, useId, useState } from 'react';
import { BaseLineLoading2 } from '@/app/components/icons/BaseLineLoading2';
import type { TreeNode } from '@/app/components/ui/tree-view';
import { TreeNodeType, TreeView } from '@/app/components/ui/tree-view';
import type { FileItem, FolderTreeNode } from '../../../types';
import type { FolderData, RenameState, RenameStateData } from '../types';
import { createRootFolder } from '../utils';
import { LoadingTrigger } from './LoadingTrigger';
import { TreeNodeLabel } from './TreeNodeLabel';

const LOADING_NODE_PREFIX = 'loading-';

interface FolderTreeProps {
  rootFolderId: string;
  rootFolderName: string;
  folderDataMap: Map<string, FolderData>;
  selectedFolder: FolderTreeNode;
  loading: boolean;
  onNodeSelect: (node: FolderTreeNode) => void;
  loadFolderChildren: (folderId: string, pageToken?: string) => void;
  onFilesUpdate?: (updater: (files: FileItem[]) => FileItem[]) => void;
  onRenameStateChange?: (state: RenameState, data: RenameStateData) => void;
}

export const FolderTree = ({
  rootFolderId,
  rootFolderName,
  folderDataMap,
  selectedFolder,
  loading,
  onNodeSelect,
  loadFolderChildren,
  onFilesUpdate,
  onRenameStateChange,
}: FolderTreeProps) => {
  // 内部状态管理
  const scrollContainerId = useId();
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set([rootFolderId]));

  // 重命名状态管理
  const handleRenameStateChange = (state: RenameState, data: RenameStateData) => {
    onRenameStateChange?.(state, data);
  };

  // 内部查找节点逻辑
  const findNode = (nodeId: string): FolderTreeNode | null => {
    if (nodeId === rootFolderId) {
      return createRootFolder({
        id: rootFolderId,
        name: rootFolderName,
      });
    }

    for (const folderData of Array.from(folderDataMap.values())) {
      const found = folderData.children.find((child: FolderTreeNode) => child.id === nodeId);
      if (found) return found;
    }
    return null;
  };

  // 处理节点展开
  const handleNodeExpand = (nodeId: string, expanded: boolean) => {
    setTimeout(() => {
      setExpandedFolders((prev) => {
        const newSet = new Set(prev);
        if (expanded) {
          newSet.add(nodeId);
        } else {
          newSet.delete(nodeId);
        }
        return newSet;
      });

      // 如果展开且没有数据，自动加载
      if (expanded && !folderDataMap.has(nodeId)) {
        loadFolderChildren(nodeId);
      }
    }, 0);
  };

  // 处理加载更多
  const handleLoadMore = useCallback(
    (folderId: string) => {
      const folderData = folderDataMap.get(folderId);
      if (folderData?.hasMore && !folderData.loading && folderData.nextPageToken) {
        loadFolderChildren(folderId, folderData.nextPageToken);
      }
    },
    [folderDataMap, loadFolderChildren],
  );

  // 如果当前选中的文件夹的父节点未展开，则改为选中其父节点（目前只有新建文件夹会有这样的情况）
  useEffect(() => {
    if (expandedFolders.has(selectedFolder.parent_id)) {
      return;
    }

    handleNodeSelect(selectedFolder.parent_id);
  }, [selectedFolder.parent_id]);

  // 转换为树形数据的内部函数
  const convertToTreeData = (
    rootId: string,
    rootName: string,
    dataMap: Map<string, FolderData>,
    expanded: Set<string>,
  ): TreeNode[] => {
    const convertToTreeNode = (node: FolderTreeNode): TreeNode => {
      const folderData = dataMap.get(node.id);
      const isExpanded = expanded.has(node.id);
      let children: TreeNode[] = [];

      if (folderData?.children && isExpanded) {
        // 添加子文件夹
        children = folderData.children.map(convertToTreeNode);

        // 如果有更多数据需要加载，添加 LoadingTrigger
        if (folderData.hasMore) {
          children.push({
            id: `${LOADING_NODE_PREFIX}${node.id}`,
            label: '',
            type: TreeNodeType.FILE,
            icon: <LoadingTrigger key={`loading-trigger-${node.id}`} folderId={node.id} onTrigger={handleLoadMore} />,
          });
        }
      }

      return {
        id: node.id,
        label: (
          <TreeNodeLabel
            node={node}
            onRenameStateChange={handleRenameStateChange}
            onFilesUpdate={onFilesUpdate ? onFilesUpdate : undefined}
          />
        ),
        type: TreeNodeType.FOLDER,
        children: children.length > 0 ? children : undefined,
        expandable: node.folder_info?.has_sub_folder ?? false,
      };
    };

    const rootData = dataMap.get(rootId);
    const rootChildren = rootData?.children || [];
    const rootNodeChildren = rootChildren.map(convertToTreeNode);

    // 为根文件夹也添加 LoadingTrigger
    if (rootData?.hasMore) {
      rootNodeChildren.push({
        id: `${LOADING_NODE_PREFIX}${rootId}`,
        label: '',
        type: TreeNodeType.FILE,
        icon: <LoadingTrigger key={`loading-trigger-${rootId}`} folderId={rootId} onTrigger={handleLoadMore} />,
      });
    }

    return [
      {
        id: rootId,
        label: rootName,
        type: TreeNodeType.FOLDER,
        children: rootNodeChildren,
        expandable: true, // 根文件夹总是可以展开
      },
    ];
  };

  const treeData = convertToTreeData(rootFolderId, rootFolderName, folderDataMap, expandedFolders);

  // 处理节点选择
  const handleNodeSelect = (nodeId: string) => {
    if (nodeId.startsWith(LOADING_NODE_PREFIX)) {
      return;
    }

    const foundNode = findNode(nodeId);
    if (foundNode) {
      onNodeSelect(foundNode);
    }
  };

  // 处理选择变化
  const handleSelectionChange = (selectedIds: string[]) => {
    const selectedId = selectedIds[0];
    if (selectedId) {
      handleNodeSelect(selectedId);
    }
  };

  // 处理节点点击
  const handleNodeClick = (node: TreeNode) => {
    handleNodeSelect(node.id);
  };

  return (
    <div id={scrollContainerId} className="h-[315px] max-h-[80vh] overflow-y-auto rounded-8 bg-[#13161A] pt-2">
      {loading && !folderDataMap.has(rootFolderId) ? (
        <BaseLineLoading2 className="mx-auto animate-spin" />
      ) : (
        <TreeView
          data={treeData}
          selectedIds={[selectedFolder.id]}
          onSelectionChange={handleSelectionChange}
          onNodeExpand={handleNodeExpand}
          onNodeClick={handleNodeClick}
          defaultExpandedIds={[rootFolderId]}
          showLines={false}
        />
      )}
    </div>
  );
};
